/**
 * @module stores/authStore
 * @description Authentication state management for Preparo
 *
 * Manages authentication state via Supabase proxy mode including:
 * - Supabase session management
 * - User ID tracking
 * - Authentication status
 * - Login/logout flows
 *
 * All authentication goes through Spectral Cloud Portal (Supabase proxy).
 *
 * @example
 * import { authStore, isAuthenticated, logout } from '@stores/authStore';
 *
 * // Check authentication
 * $: authenticated = $isAuthenticated;
 *
 * // Logout
 * logout();
 */

import { writable, derived, get } from 'svelte/store';
import { createLogger } from '@services/loggerService';
import {
  isAuthenticated as checkSupabaseAuth,
  isPandocConfigured as checkPandocConfigured,
  isZoteroConfigured as checkZoteroConfigured,
  signInWithEmail,
  handleSsoFromUrl,
  signOut as supabaseSignOut,
  getPortalUrl,
  getReturnUrl,
} from '@services/supabaseClient';
import { api } from '@/config';

const log = createLogger('auth');

// =============================================================================
// TYPES
// =============================================================================

/** Authentication state */
export interface AuthState {
  /** User ID from Supabase */
  userId: string | null;
  /** User email */
  email: string | null;
  /** Whether authentication is in progress */
  isLoading: boolean;
  /** Authentication error message */
  error: string | null;
  /** Proxy mode state */
  proxy: {
    /** Whether Supabase session is active */
    isSupabaseAuthenticated: boolean;
    /** Whether Pandoc server is configured */
    isPandocConfigured: boolean;
    /** Whether Zotero API key is configured */
    isZoteroConfigured: boolean;
  };
}

/** Login result */
export interface LoginResult {
  success: boolean;
  error?: string;
  userId?: string;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: AuthState = {
  userId: null,
  email: null,
  isLoading: false,
  error: null,
  proxy: {
    isSupabaseAuthenticated: false,
    isPandocConfigured: false,
    isZoteroConfigured: false,
  },
};

// =============================================================================
// STORE
// =============================================================================

/** Internal writable store */
const store = writable<AuthState>(initialState);

/** Exported auth store (read-only subscribe) */
export const authStore = {
  subscribe: store.subscribe,
};

// =============================================================================
// DERIVED STORES
// =============================================================================

/** Whether user is authenticated */
export const isAuthenticated = derived(
  store,
  ($store) => $store.proxy.isSupabaseAuthenticated
);

/** Whether authentication is loading */
export const isAuthLoading = derived(store, ($store) => $store.isLoading);

/** Current user ID */
export const userId = derived(store, ($store) => $store.userId);

/** Current user email */
export const userEmail = derived(store, ($store) => $store.email);

/** Authentication error */
export const authError = derived(store, ($store) => $store.error);

/** Whether proxy mode is enabled */
export const isProxyMode = derived(store, () => api.proxy.enabled);

/** Whether Supabase is authenticated (proxy mode) */
export const isSupabaseAuthenticated = derived(
  store,
  ($store) => $store.proxy.isSupabaseAuthenticated
);

/** Whether Pandoc is configured (proxy mode) */
export const isPandocConfigured = derived(store, ($store) => $store.proxy.isPandocConfigured);

/** Whether Zotero is configured (proxy mode) */
export const isZoteroConfigured = derived(store, ($store) => $store.proxy.isZoteroConfigured);

// =============================================================================
// ACTIONS
// =============================================================================

/**
 * Initialize auth (checks Supabase session and SSO tokens)
 */
export async function initAuth(): Promise<void> {
  if (!api.proxy.enabled) {
    log.info('Proxy mode disabled, skipping auth initialization');
    return;
  }

  log.info('Initializing authentication');
  store.update((s) => ({ ...s, isLoading: true, error: null }));

  try {
    // First, check for SSO tokens in URL (from Portal redirect)
    await handleSsoFromUrl();

    const isSupabaseAuth = await checkSupabaseAuth();
    const isPandocConf = isSupabaseAuth ? await checkPandocConfigured() : false;
    const isZoteroConf = isSupabaseAuth ? await checkZoteroConfigured() : false;

    store.update((s) => ({
      ...s,
      isLoading: false,
      proxy: {
        isSupabaseAuthenticated: isSupabaseAuth,
        isPandocConfigured: isPandocConf,
        isZoteroConfigured: isZoteroConf,
      },
    }));

    log.info('Auth check complete', { isSupabaseAuth, isPandocConf, isZoteroConf });
  } catch (error) {
    log.error('Auth check failed', { error });
    store.update((s) => ({
      ...s,
      isLoading: false,
      error: error instanceof Error ? error.message : 'Authentication check failed',
    }));
  }
}

/**
 * Refresh authentication status
 */
export async function refreshAuth(): Promise<void> {
  await initAuth();
}

/**
 * Login to Supabase with email and password
 */
export async function loginToSupabase(
  email: string,
  password: string
): Promise<{ success: boolean; error?: string }> {
  log.info('Attempting Supabase login');
  store.update((s) => ({ ...s, isLoading: true, error: null }));

  try {
    const result = await signInWithEmail(email, password);

    if (result.success) {
      // Check Pandoc and Zotero configuration after successful login
      const isPandocConf = await checkPandocConfigured();
      const isZoteroConf = await checkZoteroConfigured();

      store.update((s) => ({
        ...s,
        isLoading: false,
        proxy: {
          isSupabaseAuthenticated: true,
          isPandocConfigured: isPandocConf,
          isZoteroConfigured: isZoteroConf,
        },
      }));

      log.info('Supabase login successful');
      return { success: true };
    } else {
      store.update((s) => ({
        ...s,
        isLoading: false,
        error: result.error ?? null,
      }));

      return { success: false, error: result.error };
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Login failed';
    store.update((s) => ({
      ...s,
      isLoading: false,
      error: message,
    }));

    log.error('Supabase login failed', { error });
    return { success: false, error: message };
  }
}

/**
 * Redirect to Portal for authentication
 */
export function redirectToPortal(): void {
  const portalUrl = getPortalUrl();
  const returnTo = getReturnUrl();
  window.location.href = `${portalUrl}/?returnTo=${returnTo}#/auth/login`;
}

/**
 * Redirect to Portal settings page
 */
export function redirectToPortalSettings(): void {
  const portalUrl = getPortalUrl();
  const returnTo = getReturnUrl();
  window.location.href = `${portalUrl}/?returnTo=${returnTo}#/settings`;
}

/**
 * Logout and clear session
 */
export async function logout(): Promise<void> {
  log.info('Logging out');

  try {
    await supabaseSignOut();
  } catch (error) {
    log.error('Error during sign out', { error });
  }

  // Reset store
  store.set({ ...initialState });

  log.info('Logged out successfully');
}

/**
 * Clear authentication error
 */
export function clearAuthError(): void {
  store.update((s) => ({ ...s, error: null }));
}

/**
 * Get current auth state (for debugging)
 */
export function getAuthState(): AuthState {
  return get(store);
}

// =============================================================================
// STORE RESET
// =============================================================================

/**
 * Reset store to initial state (for testing)
 */
export function resetAuthStore(): void {
  store.set({ ...initialState });
}

export default authStore;
